import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "./redux/store";
import { selectShops, selectAppointments } from "./redux/selectors";
import ReactGA4 from "react-ga4";
import TagManager from "react-gtm-module";
import tinycolor from "tinycolor2";
import { formatPhoneNumber } from "./utils";
import faIconsCss from "virtual:fa-icons";
import widgetCss from "./css/index.css?inline";

import { InitOptions } from "react-ga4/types/ga4";
import { AppointmentState } from "./types";
import {
  AppointmentConfirmed,
  AppointmentSubmitting,
  AppointmentBookingFailed,
  AppointmentPickerV2,
  CustomerInput,
  ServiceOptions,
  ShopLocations,
  SkipToForm,
  StayOrGo,
  VehicleSelection,
} from "./pages";
import Header from "./Header";
import Footer from "./Footer";
import {
  BookingStage,
  isBookingFlowStage,
} from "./constants/bookingStages";

// Inject the styles (generated icon CSS first, then the app CSS) from the
// entry script itself, as the CRA style-loader build did, so no separate
// stylesheet request is needed.
const widgetStyle = document.createElement("style");
widgetStyle.textContent = faIconsCss + widgetCss;
document.head.appendChild(widgetStyle);

// Define interfaces for the component props and data structures
interface PopupWidgetProps {
  loading?: boolean;
}

interface ShopLocation {
  // Define shop location properties
  id: string;
  name: string;
  // Add other properties as needed
}

const PopupWidget: React.FC<PopupWidgetProps> = ({ loading = false }) => {
  const [ga4Loaded, setGa4Loaded] = useState<boolean>(false);
  const [gtmLoaded, setGtmLoaded] = useState<boolean>(false);
  const stageScrollRef = useRef<HTMLDivElement | null>(null);
  const outerScrollRef = useRef<HTMLDivElement | null>(null);
  const shops = useSelector(selectShops) as ShopLocation[];

  const appointment = useSelector(selectAppointments) as AppointmentState;
  const isStageZero = appointment?.stage === BookingStage.Location;
  const shopConfig = appointment?.shopData;
  const headerColor = isStageZero
    ? "#ffffff"
    : shopConfig?.headerColor || "#ffffff";
  const isHeaderDark = tinycolor(headerColor).isDark();
  const stageOnePlusFooterTextColor = isHeaderDark ? "#ffffff" : "#4b5563";
  const stageOnePlusFooterBorderColor = isHeaderDark
    ? "rgba(255,255,255,0.25)"
    : "#d1d5db";
  const stageOnePlusFooterBackground = headerColor;
  const stageZeroTextColor = "#2b73b9";
  const stageZeroSubtitleColor = "#4b5563";

  const initializeGA4 = useCallback((): void => {
    if (
      appointment?.shopData?.pixels?.googleAnalytics?.gTag !== undefined &&
      !ga4Loaded
    ) {
      const gTags = [
        { trackingId: appointment.shopData.pixels.googleAnalytics.gTag },
      ] as InitOptions[];

      ReactGA4.initialize(gTags);
      setGa4Loaded(true);
    }
  }, [appointment, ga4Loaded]);

  const initializeGTM = useCallback((): void => {
    if (appointment?.shopData?.pixels?.googleTagManager?.GTMTag && !gtmLoaded) {
      const dcTagManagerArgs = {
        gtmId: appointment.shopData.pixels.googleTagManager.GTMTag,
      };
      TagManager.initialize(dcTagManagerArgs);
      setGtmLoaded(true);
    }
  }, [appointment, gtmLoaded]);

  useEffect(() => {
    initializeGA4();
    initializeGTM();
  }, [appointment, initializeGA4, initializeGTM]);

  useEffect(() => {
    stageScrollRef.current?.scrollTo({ top: 0, behavior: "auto" });
    outerScrollRef.current?.scrollTo({ top: 0, behavior: "auto" });
  }, [appointment?.stage]);

  // Render the appropriate component based on the current stage
  const renderStageContent = (): React.JSX.Element | null => {
    if (!appointment) return null;

    switch (appointment.stage) {
      case BookingStage.Location:
        return <ShopLocations data={shops} appointment={appointment} />;
      case BookingStage.ServiceSelection:
        return <ServiceOptions appointment={appointment} />;
      case BookingStage.AppointmentOptions:
        return <AppointmentPickerV2 appointment={appointment} />;
      case BookingStage.CustomerInfo:
        return <CustomerInput appointment={appointment} />;
      case BookingStage.VehicleInfo:
        return <VehicleSelection appointment={appointment} />;
      case BookingStage.Confirmed:
        return <AppointmentConfirmed appointment={appointment} />;
      case BookingStage.Bailout:
        return <StayOrGo appointment={appointment} />;
      case BookingStage.SkipToForm:
        return <SkipToForm appointment={appointment} />;
      case BookingStage.Submitting:
        return <AppointmentSubmitting appointment={appointment} />;
      case BookingStage.BookingFailed:
        return <AppointmentBookingFailed appointment={appointment} />;
      default:
        return null;
    }
  };

  // Loading state component
  const renderLoading = (): React.JSX.Element => (
    <div
      style={{
        height: "416px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div className="flex justify-center items-center py-12">
        <div className="flex flex-col items-center text-center">
          <i
            className="fa-solid fa-tire-rugged animate-spin text-6xl"
            aria-hidden="true"
            style={{ color: stageZeroTextColor, lineHeight: 1 }}
          />
          <div
            className="mt-4 text-sm md:text-base"
            style={{ color: stageZeroSubtitleColor }}
          >
            Fetching shop locations...
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Full-page overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-70"
        style={{
          zIndex: "999 !important",
          background:
            "linear-gradient(0deg, rgba(255, 255, 255, 0.8) 16%, rgba(212,213,212,0.8) 74%, rgba(194,194,194,0.8) 100%)",
        }}
      ></div>

      {/* Popup container */}
      <div
        className="fixed inset-0 flex items-center justify-center w-full"
        style={{ zIndex: 1000 }}
      >
        <div
          className="bg-white font-roboto text-black shadow-lg md:rounded-[15px] w-full h-full max-h-full md:h-auto md:max-h-[calc(100vh-120px)] md:w-[800px] overflow-hidden"
        >
        <div
          ref={outerScrollRef}
          className="h-full overflow-hidden md:overflow-y-auto md:max-h-[calc(100vh-120px)] md:rounded-[15px]"
        >
          <div className="flex flex-col h-full md:h-auto">
            <div
              className={`top-0 sticky z-10 flex-shrink-0 ${
                isBookingFlowStage(appointment?.stage)
                  ? "bg-transparent shadow-sm"
                  : "bg-white shadow-lg"
              }`}
            >
              <Header appointment={appointment} />
            </div>

            <div
              ref={stageScrollRef}
              className={`flex-1 min-h-0 md:flex-none md:overflow-visible overflow-y-auto ${
                appointment?.stage === BookingStage.ServiceSelection
                  ? "stage-scroll"
                  : ""
              }`}
              style={{
                background: isBookingFlowStage(appointment?.stage)
                  ? "#ffffff"
                  : "linear-gradient(0deg, rgba(255,255,255,1) 16%, rgba(212,213,212,1) 74%, rgba(194,194,194,1) 100%)",
              }}
              data-testid="booking-stage"
              data-stage={appointment?.stage ?? -1}
            >
              {shopConfig && (shopConfig as any).enabled === false ? (
                <div className="flex items-center justify-center w-full h-full">
                  <div className="p-20 max-w-md w-full text-center m-auto">
                    <h2 className="text-xl font-semibold mb-4">
                      Online appointments are not currently available.
                    </h2>
                    {shopConfig.shopContact?.phone?.number && (
                      <p className="mb-2">
                        Please call{" "}
                        <a
                          href={`tel:${shopConfig.shopContact.phone.number}`}
                          className="text-blue-600 hover:underline"
                        >
                          {formatPhoneNumber(
                            shopConfig.shopContact.phone.number
                          )}
                        </a>{" "}
                        to book an appointment.
                      </p>
                    )}
                  </div>
                </div>
              ) : loading ? (
                renderLoading()
              ) : (
                <div
                  className={
                    isBookingFlowStage(appointment?.stage)
                      ? "h-full min-h-0"
                      : undefined
                  }
                >
                  {renderStageContent()}
                </div>
              )}
            </div>
            <div className="flex-shrink-0">
              {appointment && (
                <Footer
                  appointment={appointment}
                  headerColor={headerColor}
                  stageOnePlusFooterTextColor={stageOnePlusFooterTextColor}
                  stageOnePlusFooterBorderColor={stageOnePlusFooterBorderColor}
                  stageOnePlusFooterBackground={stageOnePlusFooterBackground}
                />
              )}
            </div>
          </div>
        </div>
        </div>
      </div>
    </>
  );
};

export default PopupWidget;
